import { useState } from 'react';
import { api, ApiError, type SiteDetail } from '../lib/api';
import { Alert, Button, Card, Field, SectionTitle, Select, Toggle } from '../components/ui';
import { PLANS, PLAN_ORDER, alsEuro, empfohlenerPlan } from '@ilc/shared';

/**
 * Einstellungen einer Website.
 *
 * Alles, was den Betrieb betrifft und nicht zum Aussehen des Banners gehört:
 * Domains, Gültigkeit der Einwilligung, Aufbewahrung des Nachweises,
 * Scan-Turnus und Status.
 */
export function SettingsTab({ site, onSaved }: { site: SiteDetail; onSaved: () => Promise<void> }) {
  const [name, setName] = useState(site.name);
  const [domain, setDomain] = useState(site.domain);
  const [additional, setAdditional] = useState(site.additionalDomains.join('\n'));
  const [lifetime, setLifetime] = useState(site.consentLifetimeDays);
  const [retention, setRetention] = useState(site.consentRetentionMonths);
  const [scanInterval, setScanInterval] = useState(site.scanIntervalDays);
  const [consentMode, setConsentMode] = useState(site.googleConsentMode);
  const [consentModeAdvanced, setConsentModeAdvanced] = useState(site.googleConsentModeAdvanced);
  const [paused, setPaused] = useState(site.status !== 'active');
  const [plan, setPlan] = useState<string>(site.plan ?? '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(extra: Record<string, unknown> = {}) {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await api.updateSite(site.id, {
        name,
        domain,
        additionalDomains: additional
          .split('\n')
          .map((entry) => entry.trim())
          .filter(Boolean),
        consentLifetimeDays: lifetime,
        consentRetentionMonths: retention,
        scanIntervalDays: scanInterval,
        googleConsentMode: consentMode,
        googleConsentModeAdvanced: consentModeAdvanced,
        status: paused ? 'paused' : 'active',
        plan: plan === '' ? null : plan,
        ...extra,
      });
      await onSaved();
      setMessage('Gespeichert. Änderungen am Banner sind nach spätestens 5 Minuten live.');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Speichern fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {error && <Alert tone="error">{error}</Alert>}
      {message && <Alert tone="info">{message}</Alert>}

      <Card>
        <SectionTitle title="Website" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" value={name} onChange={(event) => setName(event.target.value)} />
          <Field
            label="Hauptdomain"
            value={domain}
            onChange={(event) => setDomain(event.target.value)}
            hint="Ohne https:// und ohne www."
          />
        </div>
        <div className="mt-4 flex flex-col gap-1.5">
          <label htmlFor="weitere-domains" className="text-sm font-medium">
            Weitere Domains
          </label>
          <textarea
            id="weitere-domains"
            rows={3}
            value={additional}
            onChange={(event) => setAdditional(event.target.value)}
            placeholder={'staging.beispiel.de'}
            className="tabular w-full rounded-xl border border-[var(--border)] bg-[var(--surface)] p-3 text-sm outline-none focus:border-brand-500"
          />
          <p className="text-xs text-[var(--text-muted)]">
            Eine je Zeile. Nur von diesen Domains und ihren Subdomains nimmt das Consent-Log Meldungen an.
          </p>
        </div>
      </Card>

      <Card>
        <SectionTitle title="Einwilligung" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Gültigkeitsdauer in Tagen"
            type="number"
            min={1}
            max={365}
            value={lifetime}
            onChange={(event) => setLifetime(Number(event.target.value))}
            hint="Danach wird erneut gefragt. Mehr als 12 Monate halten Aufsichtsbehoerden für erklärungsbedürftig."
          />
          <Field
            label="Nachweis aufbewahren (Monate)"
            type="number"
            min={1}
            max={60}
            value={retention}
            onChange={(event) => setRetention(Number(event.target.value))}
            hint="Aeltere Einträge werden nächtlich gelöscht (Art. 5 Abs. 1 lit. e DSGVO)."
          />
        </div>

        <div className="mt-4 divide-y divide-[var(--border)]">
          <Toggle
            label="Google Consent Mode v2"
            description="Setzt die Signale für Google-Dienste, bevor ein Tag lädt. Für Google Ads und GA4 praktisch Pflicht."
            checked={consentMode}
            onChange={setConsentMode}
          />
          <Toggle
            label="Consent Mode im erweiterten Modus"
            description="Google darf vor der Einwilligung cookielose Pings senden. Eine Entscheidung des Kunden, nicht unsere Voreinstellung."
            checked={consentModeAdvanced}
            onChange={setConsentModeAdvanced}
          />
        </div>

        <div className="mt-5 rounded-xl border border-[var(--border)] p-4">
          <p className="text-sm font-medium">Erneut fragen</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">
            Wenn neue Zwecke dazukommen, deckt die alte Einwilligung sie nicht ab. Das setzt die Version hoch und holt bei
            jedem Besucher eine neue Entscheidung ein. Aktuell Version {site.consentVersion}.
          </p>
          <Button
            variant="secondary"
            size="sm"
            className="mt-3"
            loading={busy}
            onClick={() => {
              if (window.confirm('Alle bestehenden Einwilligungen verlieren ihre Gültigkeit. Fortfahren?')) {
                void save({ bumpConsentVersion: true });
              }
            }}
          >
            Neue Einwilligung einholen
          </Button>
        </div>
      </Card>

      <Card>
        <SectionTitle
          title="Tarif"
          hint="Abgerechnet wird je Domain nach der Zahl der Unterseiten. Eine Änderung passiert nie von selbst."
        />
        <Select label="Tarif dieser Domain" value={plan} onChange={(event) => setPlan(event.target.value)}>
          <option value="">Tarif des Mandanten verwenden</option>
          {PLAN_ORDER.map((key) => (
            <option key={key} value={key}>
              {PLANS[key].label}, {alsEuro(PLANS[key].priceCents)} je Monat, bis {PLANS[key].subpageLimit.toLocaleString('de-DE')} Unterseiten
            </option>
          ))}
        </Select>
        {site.subpageCount !== null && site.subpageCount !== undefined && (
          <p className="mt-2 text-xs text-[var(--text-muted)]">
            Zuletzt gemessen: {site.subpageCount.toLocaleString('de-DE')} Unterseiten. Dazu passt der Tarif{' '}
            <strong>{empfohlenerPlan(site.subpageCount).label}</strong>. Das ist ein Vorschlag, keine Umstellung.
          </p>
        )}
      </Card>

      <Card>
        <SectionTitle title="Scan" />
        <Field
          label="Turnus in Tagen"
          type="number"
          min={0}
          max={365}
          value={scanInterval}
          onChange={(event) => setScanInterval(Number(event.target.value))}
          hint="0 bedeutet nur von Hand. Empfohlen sind 30 Tage, damit ein neues Plugin nicht unbemerkt Cookies setzt."
        />
      </Card>

      <Card>
        <SectionTitle title="Status" />
        <Toggle
          label="Website pausieren"
          description="Das Banner wird nicht mehr ausgeliefert."
          checked={paused}
          onChange={setPaused}
        />
        {paused && (
          <div className="mt-3">
            <Alert tone="warn">
              Solange pausiert ist, liefert der Loader eine 404 aus und es wird nichts blockiert. Die Website des Kunden
              läuft dann ohne Einwilligungslösung. Nur für Wartung gedacht.
            </Alert>
          </div>
        )}
      </Card>

      <div>
        <Button onClick={() => void save()} loading={busy}>
          Speichern
        </Button>
      </div>
    </div>
  );
}
